import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send } from "lucide-react";
import { api } from "@/lib/api";

const SUGGESTIONS = [
  "What services do you offer?",
  "How much does an AI chatbot cost?",
  "How long until launch?",
  "Can you integrate AI into my site?",
];

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Nova ✦ — CODER's AI assistant. Ask me about services, pricing or our process." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message: content });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "I'm having trouble right now. Please WhatsApp CODER at +91 6260943446 — reply comes quickly." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        aria-label="Open Nova AI chatbot"
        data-testid="chatbot-toggle"
        onClick={() => setOpen(!open)}
        className="fixed bottom-20 right-6 z-[60] w-14 h-14 grid place-items-center rounded-full bg-nova-gold text-nova-bg shadow-2xl animate-pulse-ring hover:scale-105 transition-transform"
      >
        {open ? <X className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            data-testid="chatbot-panel"
            className="fixed bottom-40 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[80vh] flex flex-col glass rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
              <div className="w-9 h-9 grid place-items-center rounded-full bg-nova-gold/15">
                <Sparkles className="w-4 h-4 text-nova-gold" />
              </div>
              <div className="flex-1">
                <div className="font-display text-nova-text">Nova ✦ AI</div>
                <div className="mono-label text-emerald-400">● Online</div>
              </div>
              <button aria-label="Close chat" data-testid="chatbot-close" onClick={() => setOpen(false)} className="text-nova-mute hover:text-nova-text">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div ref={bodyRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-msg-${m.role}-${i}`}
                    className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-nova-gold text-nova-bg rounded-br-sm"
                        : "bg-white/5 text-nova-text rounded-bl-sm border border-white/5"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="px-4 py-3 rounded-2xl bg-white/5 border border-white/5 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-nova-gold rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-nova-gold rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-nova-gold rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}

              {messages.length <= 1 && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`chat-suggestion-${s.slice(0, 10).replace(/\s+/g, '-').toLowerCase()}`}
                      className="text-xs px-3 py-1.5 border border-white/10 text-nova-mute hover:text-nova-text hover:border-nova-gold/40 rounded-full transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-white/5 p-3 flex items-center gap-2"
              data-testid="chatbot-form"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me anything…"
                className="flex-1 bg-transparent text-sm text-nova-text placeholder-nova-mute px-3 py-2 focus:outline-none"
                data-testid="chatbot-input"
              />
              <button
                type="submit"
                aria-label="Send"
                disabled={!input.trim() || loading}
                data-testid="chatbot-send"
                className="w-9 h-9 grid place-items-center rounded-full bg-nova-gold text-nova-bg disabled:opacity-40 hover:bg-[#C9F9D6] transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
